import { createRoute, useNavigate } from "@tanstack/react-router";
import { withPermission } from "../lib/with-permission";
import { useTranslation } from "react-i18next";
import { useState, useCallback, useEffect } from "react";
import { Plus, Minus } from "lucide-react";
import type { SearchableSelectOption } from "../components/ui/SearchableSelect/SearchableSelect";
import { rootRoute } from "./__root";
import { PageHeader } from "../components/ui/PageHeader/PageHeader";
import { Card } from "../components/ui/Card/Card";
import { Button } from "../components/ui/Button/Button";
import { Input } from "../components/ui/Input/Input";
import { Select } from "../components/ui/Select/Select";
import { FormField } from "../components/ui/FormField/FormField";
import { SearchableSelect } from "../components/ui/SearchableSelect/SearchableSelect";
import { useCreateOrder, useUpdateOrder, useOrderDetail } from "../hooks/use-orders";
import { useFormDraft } from "../hooks/use-form-draft";
import { api } from "../lib/api";
import styles from "./OrderNew.module.css";

export const route = createRoute({
	getParentRoute: () => rootRoute,
	path: "/orders/new",
	component: withPermission(OrderNewPage, "orders", "create"),
});

interface StockItemWarehouse {
	warehouseId: string;
	warehouseName: string;
	warehouseCode: string;
	currentStock: string | number;
}

interface LineItem {
	lineNumber: number;
	itemCode: string;
	itemDescription: string;
	displayDescription: string;
	quantity: string;
	unit: string;
	unitPrice: string;
	vatRate: string;
	vatGroup: string;
	discountPercent: string;
	notes: string;
	stockItemId: string;
	warehouseId: string;
}

function OrderNewPage() {
	const search = route.useSearch() as {
		companyId?: string;
		contactPersonId?: string;
		sourceOfferId?: string;
	};
	return <OrderFormPage initialSearch={search} />;
}

export function OrderFormPage({
	editId,
	initialSearch,
}: {
	editId?: string;
	initialSearch?: {
		companyId?: string;
		contactPersonId?: string;
		sourceOfferId?: string;
	};
}) {
	const { t } = useTranslation();
	const navigate = useNavigate();
	const createOrder = useCreateOrder();
	const updateOrder = useUpdateOrder();
	const isEdit = !!editId;
	const detail = useOrderDetail(editId ?? "");
	const [prefilled, setPrefilled] = useState(false);
	const search = initialSearch ?? {};
	const [header, setHeader] = useState({
		companyId: search.companyId ?? "",
		contactPersonId: search.contactPersonId ?? "",
		sourceOfferId: search.sourceOfferId ?? "",
		orderDate: "",
		deliveryDate: "",
		currency: "TL",
		paymentTerms: "",
		deliveryTerms: "",
		notes: "",
		startupDescription: "",
		installationDescription: "",
		tmsDescription: "",
		atsDescription: "",
		factorySpecialNotes: "",
		generalNotes: "",
		status: "confirmed",
		isCustomDesign: false,
	});
	const [initialOptions, setInitialOptions] = useState<
		Record<string, SearchableSelectOption | undefined>
	>({});
	const [formError, setFormError] = useState("");
	const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({});

	useEffect(() => {
		const opts: Record<string, SearchableSelectOption> = {};
		const fetches: Promise<void>[] = [];
		if (header.companyId) {
			fetches.push(
				api
					.get<{ id: string; legalName: string; currency: string | null }>(
						`/api/companies/${header.companyId}`,
					)
					.then((c) => {
						opts.company = {
							value: c.id,
							label: `${c.legalName}${c.currency ? ` (${c.currency})` : ""}`,
						};
					})
					.catch(() => {}),
			);
		}
		if (header.contactPersonId) {
			fetches.push(
				api
					.get<{ id: string; firstName: string; lastName: string }>(
						`/api/persons/${header.contactPersonId}`,
					)
					.then((p) => {
						opts.person = { value: p.id, label: `${p.firstName} ${p.lastName}` };
					})
					.catch(() => {}),
			);
		}
		if (header.sourceOfferId) {
			fetches.push(
				api
					.get<{ id: string; documentNumber: string | null }>(`/api/offers/${header.sourceOfferId}`)
					.then((o) => {
						opts.offer = { value: o.id, label: o.documentNumber ?? o.id.slice(0, 8) };
					})
					.catch(() => {}),
			);
		}
		if (fetches.length === 0) return;
		Promise.all(fetches).then(() => setInitialOptions((prev) => ({ ...prev, ...opts })));
	}, [header.companyId, header.contactPersonId, header.sourceOfferId]);

	// Muhatap seçilince firmanın yetkili kişisi otomatik gelir.
	useEffect(() => {
		if (!header.companyId || header.contactPersonId) return;
		if (isEdit && !prefilled) return;
		let cancelled = false;
		api
			.get<{
				items: Array<{ id: string; firstName: string; lastName: string; isPrimary?: boolean }>;
			}>(`/api/persons?limit=50&companyId=${header.companyId}`)
			.then((res) => {
				if (cancelled) return;
				const primary = res.items.find((p) => p.isPrimary) ?? res.items[0];
				if (!primary) return;
				setHeader((prev) =>
					prev.companyId === header.companyId && !prev.contactPersonId
						? { ...prev, contactPersonId: primary.id }
						: prev,
				);
				setInitialOptions((prev) => ({
					...prev,
					person: { value: primary.id, label: `${primary.firstName} ${primary.lastName}` },
				}));
			})
			.catch(() => {});
		return () => {
			cancelled = true;
		};
	}, [header.companyId, header.contactPersonId, isEdit, prefilled]);
	const [lines, setLines] = useState<LineItem[]>([
		{
			lineNumber: 1,
			itemCode: "",
			itemDescription: "",
			displayDescription: "",
			quantity: "1",
			unit: "",
			unitPrice: "",
			vatRate: "20",
			vatGroup: "",
			discountPercent: "0",
			notes: "",
			stockItemId: "",
			warehouseId: "",
		},
	]);
	const [warehouseStocks, setWarehouseStocks] = useState<Record<number, StockItemWarehouse[]>>({});
	const [allWarehouses, setAllWarehouses] = useState<{ id: string; code: string; name: string }[]>(
		[],
	);

	useEffect(() => {
		void (async () => {
			try {
				const res = await api.get<{
					items: { id: string; code: string; name: string; isActive: boolean }[];
				}>(`/api/warehouses?limit=200`);
				setAllWarehouses(
					(res.items ?? [])
						.filter((w) => w.isActive)
						.map((w) => ({ id: w.id, code: w.code, name: w.name })),
				);
			} catch {
				setAllWarehouses([]);
			}
		})();
	}, []);

	const [vatGroups, setVatGroups] = useState<{ code: string; name: string; rate: number }[]>([]);
	useEffect(() => {
		void (async () => {
			try {
				const res =
					await api.get<{ code: string; name: string; rate: number }[]>(`/api/sap/vat-groups`);
				setVatGroups(res ?? []);
			} catch {
				setVatGroups([]);
			}
		})();
	}, []);

	interface OrderDraft {
		header: typeof header;
		lines: typeof lines;
	}
	const draft = useFormDraft<OrderDraft>("order-draft", !isEdit);

	const restoreDraft = () => {
		if (!draft.pendingDraft) return;
		setHeader(draft.pendingDraft.header);
		setLines(draft.pendingDraft.lines);
		draft.acceptDraft();
	};

	useEffect(() => {
		if (draft.showPrompt) return;
		const hasData = !!(
			header.companyId ||
			header.contactPersonId ||
			header.notes ||
			lines.some((l) => l.itemCode || l.itemDescription || l.stockItemId || Number(l.unitPrice) > 0)
		);
		if (hasData) draft.save({ header, lines });
		else draft.clear();
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [header, lines, draft.showPrompt]);

	useEffect(() => {
		if (!isEdit || prefilled || !detail.data) return;
		const o = detail.data;
		const d = (v: string | null) => (v ? v.slice(0, 10) : "");
		// eslint-disable-next-line react-hooks/set-state-in-effect
		setHeader((prev) => ({
			...prev,
			companyId: o.companyId ?? "",
			contactPersonId: o.contactPersonId ?? "",
			sourceOfferId: o.sourceOfferId ?? "",
			orderDate: d(o.orderDate),
			deliveryDate: d(o.deliveryDate),
			currency: o.currency ?? prev.currency,
			paymentTerms: o.paymentTerms ?? "",
			deliveryTerms: o.deliveryTerms ?? "",
			notes: o.notes ?? "",
			startupDescription: o.startupDescription ?? "",
			installationDescription: o.installationDescription ?? "",
			tmsDescription: o.tmsDescription ?? "",
			atsDescription: o.atsDescription ?? "",
			factorySpecialNotes: o.factorySpecialNotes ?? "",
			generalNotes: o.generalNotes ?? "",
			status: o.status ?? "confirmed",
			isCustomDesign: o.isCustomDesign ?? false,
		}));
		const sorted = [...(o.lines ?? [])].sort((a, b) => a.lineNumber - b.lineNumber);
		if (sorted.length > 0) {
			setLines(
				sorted.map((l, i) => ({
					lineNumber: i + 1,
					itemCode: l.itemCode ?? "",
					itemDescription: l.itemDescription ?? "",
					displayDescription: l.displayDescription ?? "",
					quantity: l.quantity ?? "1",
					unit: l.unit ?? "",
					unitPrice: l.unitPrice ?? "",
					vatRate: l.vatRate ?? "20",
					vatGroup: l.vatGroup ?? "",
					discountPercent: l.discountPercent ?? "0",
					notes: l.notes ?? "",
					stockItemId: l.stockItemId ?? "",
					warehouseId: l.warehouseId ?? "",
				})),
			);
		}
		setPrefilled(true);
	}, [isEdit, prefilled, detail.data]);

	const addLine = () => {
		setLines([
			...lines,
			{
				lineNumber: lines.length + 1,
				itemCode: "",
				itemDescription: "",
				displayDescription: "",
				quantity: "1",
				unit: "",
				unitPrice: "",
				vatRate: "20",
				vatGroup: "",
				discountPercent: "0",
				notes: "",
				stockItemId: "",
				warehouseId: "",
			},
		]);
	};

	const removeLine = (idx: number) => {
		setLines(lines.filter((_, i) => i !== idx).map((l, i) => ({ ...l, lineNumber: i + 1 })));
		setWarehouseStocks((prev) => {
			const copy: Record<number, StockItemWarehouse[]> = {};
			Object.keys(prev).forEach((key) => {
				const k = Number(key);
				const val = prev[k];
				if (!val) return;
				if (k < idx) copy[k] = val;
				if (k > idx) copy[k - 1] = val;
			});
			return copy;
		});
	};

	const updateLine = (idx: number, field: keyof LineItem, value: string) => {
		const next = [...lines];
		next[idx] = { ...next[idx], [field]: value } as LineItem;
		setLines(next);
	};

	const calcLineTotal = (line: LineItem) => {
		const qty = Number(line.quantity) || 0;
		const price = Number(line.unitPrice) || 0;
		const vat = Number(line.vatRate) || 0;
		const disc = Number(line.discountPercent) || 0;
		const base = qty * price * (1 - disc / 100);
		return base * (1 + vat / 100);
	};

	const subtotal = lines.reduce(
		(sum, l) =>
			sum +
			(Number(l.quantity) || 0) *
				(Number(l.unitPrice) || 0) *
				(1 - (Number(l.discountPercent) || 0) / 100),
		0,
	);
	const vatTotal = lines.reduce(
		(sum, l) =>
			sum +
			(Number(l.quantity) || 0) *
				(Number(l.unitPrice) || 0) *
				(1 - (Number(l.discountPercent) || 0) / 100) *
				((Number(l.vatRate) || 0) / 100),
		0,
	);
	const total = subtotal + vatTotal;

	const loadCompanies = useCallback(async (query: string) => {
		const res = await api.get<{
			items: Array<{ id: string; legalName: string; currency: string | null }>;
		}>(`/api/companies?limit=50${query ? `&search=${encodeURIComponent(query)}` : ""}`);
		return res.items.map((c) => ({
			value: c.id,
			label: `${c.legalName}${c.currency ? ` (${c.currency})` : ""}`,
		}));
	}, []);

	const loadPersons = useCallback(
		async (query: string) => {
			const res = await api.get<{
				items: Array<{ id: string; firstName: string; lastName: string }>;
			}>(
				`/api/persons?limit=50${header.companyId ? `&companyId=${header.companyId}` : ""}${query ? `&search=${encodeURIComponent(query)}` : ""}`,
			);
			return res.items.map((p) => ({ value: p.id, label: `${p.firstName} ${p.lastName}` }));
		},
		[header.companyId],
	);

	const loadOffers = useCallback(
		async (query: string) => {
			const res = await api.get<{
				items: Array<{ id: string; documentNumber: string | null; companyName: string | null }>;
			}>(
				`/api/offers?limit=50${header.companyId ? `&companyId=${header.companyId}` : ""}${query ? `&search=${encodeURIComponent(query)}` : ""}`,
			);
			return res.items.map((o) => ({
				value: o.id,
				label: o.companyName
					? `${o.companyName} — ${o.documentNumber || o.id.slice(0, 8)}`
					: o.documentNumber || o.id.slice(0, 8),
			}));
		},
		[header.companyId],
	);

	const loadStockItems = useCallback(async (query: string) => {
		const res = await api.get<{
			items: Array<{ id: string; code: string; name: string; uom: string | null }>;
		}>(
			`/api/stock-items?limit=50&activeOnly=true${query ? `&search=${encodeURIComponent(query)}` : ""}`,
		);
		return res.items.map((s) => ({
			value: s.id,
			label: `${s.code} — ${s.name}`,
			meta: { code: s.code, name: s.name, uom: s.uom ?? "" },
		}));
	}, []);

	const handleSelectStockItem = async (
		idx: number,
		stockItemId: string,
		option?: { label: string; meta?: Record<string, string> },
	) => {
		const next = [...lines];
		const line = next[idx];
		if (!line) return;

		if (!stockItemId || !option) {
			next[idx] = {
				...line,
				stockItemId: "",
				itemCode: "",
				itemDescription: "",
				unit: "",
				warehouseId: "",
			};
			setLines(next);
			setWarehouseStocks((prev) => {
				const copy = { ...prev };
				delete copy[idx];
				return copy;
			});
			return;
		}

		const parts = option.label.split(" — ");
		const code = parts[0] ?? "";
		const name = parts[1] ?? "";
		next[idx] = {
			...line,
			stockItemId,
			itemCode: code,
			itemDescription: name,
			displayDescription: line.displayDescription || name,
			unit: option.meta?.uom || line.unit,
		};
		setLines(next);

		try {
			const res = await api.get<{
				warehouseStocks: StockItemWarehouse[];
				defaultWarehouseCode: string | null;
				salesVatGroup: string | null;
			}>(`/api/stock-items/${stockItemId}`);
			const stocks = res.warehouseStocks ?? [];
			setWarehouseStocks((prev) => ({ ...prev, [idx]: stocks }));
			// Ürünün SAP satış KDV grubunu varsayılan seç, oranı da ata
			if (res.salesVatGroup) {
				const g = vatGroups.find((v) => v.code === res.salesVatGroup);
				setLines((prevLines) => {
					const updated = [...prevLines];
					if (updated[idx]) {
						updated[idx] = {
							...updated[idx],
							vatGroup: res.salesVatGroup as string,
							...(g ? { vatRate: String(g.rate) } : {}),
						};
					}
					return updated;
				});
			}
			let defaultWhId = "";
			if (res.defaultWarehouseCode) {
				const match = allWarehouses.find((w) => w.code === res.defaultWarehouseCode);
				if (match) defaultWhId = match.id;
			}
			if (!defaultWhId && stocks.length > 0) {
				const first = stocks[0]!;
				const maxStock = stocks.reduce(
					(max, s) => (Number(s.currentStock) > Number(max.currentStock) ? s : max),
					first,
				);
				defaultWhId = maxStock.warehouseId;
			}
			if (defaultWhId) {
				setLines((prevLines) => {
					const updated = [...prevLines];
					if (updated[idx]) {
						updated[idx] = { ...updated[idx], warehouseId: defaultWhId };
					}
					return updated;
				});
			}
		} catch {
			setWarehouseStocks((prev) => {
				const copy = { ...prev };
				delete copy[idx];
				return copy;
			});
		}
	};

	const submit = () => {
		setFormError("");
		const errors: Record<string, string> = {};
		if (!header.companyId) errors.companyId = t("requiredField");
		if (!header.orderDate) errors.orderDate = t("requiredField");
		if (!header.deliveryDate) errors.deliveryDate = t("requiredField");
		setFieldErrors(errors);
		if (Object.keys(errors).length > 0) return;
		const invalidLine = lines.findIndex((l) => !l.quantity || Number(l.quantity) <= 0);
		if (invalidLine !== -1) {
			setFormError(t("fillRequiredFields"));
			return;
		}
		const payload = {
			...header,
			orderDate: header.orderDate || undefined,
			deliveryDate: header.deliveryDate || undefined,
			companyId: header.companyId || undefined,
			contactPersonId: header.contactPersonId || undefined,
			sourceOfferId: header.sourceOfferId || undefined,
			subtotal,
			vatTotal,
			total,
			lines: lines.map((l) => ({
				lineNumber: l.lineNumber,
				itemCode: l.itemCode || undefined,
				itemDescription: l.itemDescription || undefined,
				displayDescription: l.displayDescription || undefined,
				quantity: Number(l.quantity) || 1,
				unit: l.unit || undefined,
				unitPrice: Number(l.unitPrice) || 0,
				vatRate: l.vatRate ? Number(l.vatRate) : undefined,
				vatGroup: l.vatGroup || undefined,
				discountPercent: l.discountPercent ? Number(l.discountPercent) : undefined,
				lineTotal: calcLineTotal(l),
				notes: l.notes || undefined,
				stockItemId: l.stockItemId || undefined,
				warehouseId: l.warehouseId || undefined,
			})),
		};

		const onError = (err: unknown) => {
			setFormError(err instanceof Error ? err.message : t("error"));
		};

		if (isEdit && editId) {
			updateOrder.mutate(
				{ id: editId, data: payload },
				{
					onSuccess: (updated) => {
						alert(t("updateSuccess"));
						navigate({ to: "/orders/$id", params: { id: updated.id } });
					},
					onError,
				},
			);
		} else {
			createOrder.mutate(payload, {
				onSuccess: () => {
					draft.clear();
					alert(t("createSuccess"));
					navigate({
						to: "/orders",
						search: {
							page: 1,
							size: 20,
							status: "",
							search: "",
							assignedTo: undefined,
							orderDateFrom: "",
							orderDateTo: "",
						},
					});
				},
				onError,
			});
		}
	};

	return (
		<div>
			<PageHeader title={isEdit ? t("editOrder") : t("newOrder")} />

			{draft.showPrompt && (
				<Card>
					<div className={styles.draftPrompt}>
						<span>Kaydedilmemiş bir taslak bulundu. Verileriniz geri yüklensin mi?</span>
						<div className={styles.draftPromptActions}>
							<Button onClick={restoreDraft}>Evet</Button>
							<Button variant="ghost" onClick={draft.discardDraft}>
								Hayır
							</Button>
						</div>
					</div>
				</Card>
			)}

			<div className={styles.grid}>
				<Card header={t("generalInfo")}>
					<div className={styles.formGrid}>
						{formError && <div className={styles.formError}>{formError}</div>}
						<FormField label={t("company")} required>
							<SearchableSelect
								value={header.companyId}
								onChange={(val) => {
									setHeader({
										...header,
										companyId: val,
										contactPersonId: "",
										sourceOfferId: "",
									});
									setInitialOptions((prev) => ({
										...prev,
										person: undefined,
										offer: undefined,
									}));
								}}
								loadOptions={loadCompanies}
								initialOption={initialOptions.company}
								placeholder={t("selectCompany")}
								clearable
							/>
						</FormField>
						<FormField label={t("authorizedPerson")}>
							<SearchableSelect
								value={header.contactPersonId}
								onChange={(val) => setHeader({ ...header, contactPersonId: val })}
								loadOptions={loadPersons}
								initialOption={initialOptions.person}
								placeholder={t("selectPerson")}
								clearable
								disabled={!header.companyId}
							/>
						</FormField>
						<FormField label={t("offer")}>
							<SearchableSelect
								value={header.sourceOfferId}
								onChange={(val) => setHeader({ ...header, sourceOfferId: val })}
								loadOptions={loadOffers}
								initialOption={initialOptions.offer}
								placeholder={t("selectOffer")}
								clearable
								disabled={!header.companyId}
							/>
						</FormField>
						<FormField label={t("orderDate")} required>
							<Input
								type="date"
								value={header.orderDate}
								onChange={(e) => setHeader({ ...header, orderDate: e.target.value })}
								error={fieldErrors.orderDate}
							/>
						</FormField>
						<FormField label={t("deliveryDate")} required>
							<Input
								type="date"
								value={header.deliveryDate}
								onChange={(e) => setHeader({ ...header, deliveryDate: e.target.value })}
								error={fieldErrors.deliveryDate}
							/>
						</FormField>
						<FormField label={t("currency")}>
							<Select
								options={[
									{ value: "TL", label: "TL" },
									{ value: "USD", label: "USD" },
									{ value: "EUR", label: "EUR" },
									{ value: "GBP", label: "GBP" },
								]}
								value={header.currency}
								onChange={(e) => setHeader({ ...header, currency: e.target.value })}
							/>
						</FormField>
					</div>
					<div className={styles.notesRow}>
						<FormField label={t("notes")} className={styles.notesField}>
							<Input
								value={header.notes}
								onChange={(e) => setHeader({ ...header, notes: e.target.value })}
							/>
						</FormField>
						<div className={styles.checkboxRow}>
							<label className={styles.checkboxLabel}>
								<input
									type="checkbox"
									checked={header.isCustomDesign}
									onChange={(e) => setHeader({ ...header, isCustomDesign: e.target.checked })}
								/>
								<span>{t("isCustomDesign")}</span>
							</label>
						</div>
					</div>
					{editId && (
						<FormField label={t("status")}>
							<Select
								options={[
									{ value: "confirmed", label: "Açık" },
									{ value: "closed", label: "Kapalı" },
									{ value: "cancelled", label: "İptal Edildi" },
								]}
								value={header.status ?? "confirmed"}
								onChange={(e) => setHeader({ ...header, status: e.target.value })}
							/>
						</FormField>
					)}
				</Card>

				<Card header={t("sapFields")}>
					<div className={styles.formGrid}>
						<FormField label={t("startupDescription")}>
							<textarea
								className={styles.textarea}
								rows={3}
								value={header.startupDescription}
								onChange={(e) => setHeader({ ...header, startupDescription: e.target.value })}
							/>
						</FormField>
						<FormField label={t("installationDescription")}>
							<textarea
								className={styles.textarea}
								rows={3}
								value={header.installationDescription}
								onChange={(e) => setHeader({ ...header, installationDescription: e.target.value })}
							/>
						</FormField>
						<FormField label={t("tmsDescription")}>
							<textarea
								className={styles.textarea}
								rows={3}
								value={header.tmsDescription}
								onChange={(e) => setHeader({ ...header, tmsDescription: e.target.value })}
							/>
						</FormField>
						<FormField label={t("atsDescription")}>
							<textarea
								className={styles.textarea}
								rows={3}
								value={header.atsDescription}
								onChange={(e) => setHeader({ ...header, atsDescription: e.target.value })}
							/>
						</FormField>
						<FormField label={t("factorySpecialNotes")}>
							<textarea
								className={styles.textarea}
								rows={3}
								value={header.factorySpecialNotes}
								onChange={(e) => setHeader({ ...header, factorySpecialNotes: e.target.value })}
							/>
						</FormField>
						<FormField label={t("generalNotes")}>
							<textarea
								className={styles.textarea}
								rows={3}
								value={header.generalNotes}
								onChange={(e) => setHeader({ ...header, generalNotes: e.target.value })}
							/>
						</FormField>
					</div>
				</Card>

				<Card header={t("lineItems")}>
					{lines.map((line, idx) => (
						<div key={idx} className={styles.lineRow}>
							<div className={styles.lineGrid}>
								<FormField label={t("stockItem")}>
									<SearchableSelect
										value={line.stockItemId}
										onChange={(val, option) => handleSelectStockItem(idx, val, option)}
										loadOptions={loadStockItems}
										placeholder={t("selectStockItem")}
										clearable
									/>
								</FormField>
								<FormField label={t("itemCode")}>
									<Input
										placeholder={t("itemCode")}
										value={line.itemCode}
										onChange={(e) => updateLine(idx, "itemCode", e.target.value)}
									/>
								</FormField>
								<FormField label={t("itemDescription")}>
									<Input
										placeholder={t("itemDescription")}
										value={line.itemDescription}
										readOnly
										style={{ background: "var(--color-bg-sunken)", cursor: "default" }}
									/>
								</FormField>
								<FormField label={t("displayDescription")}>
									<Input
										placeholder={t("displayDescription")}
										value={line.displayDescription}
										onChange={(e) => updateLine(idx, "displayDescription", e.target.value)}
									/>
								</FormField>
								{allWarehouses.length > 0 && (
									<FormField label={t("warehouse")}>
										<Select
											options={[
												{ value: "", label: "—" },
												...allWarehouses.map((w) => {
													const stock = warehouseStocks[idx]?.find(
														(s) => s.warehouseId === w.id,
													)?.currentStock;
													return {
														value: w.id,
														label:
															stock != null
																? `${w.name} — ${t("stockQuantity")}: ${stock}`
																: w.name,
													};
												}),
											]}
											value={line.warehouseId}
											onChange={(e) => updateLine(idx, "warehouseId", e.target.value)}
										/>
									</FormField>
								)}
								<FormField label={t("quantity")}>
									<Input
										placeholder={t("quantity")}
										type="number"
										value={line.quantity}
										onChange={(e) => updateLine(idx, "quantity", e.target.value)}
									/>
								</FormField>
								<FormField label={t("unit")}>
									<Input
										placeholder={t("unit")}
										value={line.unit}
										readOnly
										disabled
										style={{ background: "var(--color-bg-sunken)", cursor: "default" }}
									/>
								</FormField>
								<FormField label={t("unitPrice")}>
									<Input
										placeholder={t("unitPrice")}
										type="number"
										value={line.unitPrice}
										onChange={(e) => updateLine(idx, "unitPrice", e.target.value)}
									/>
								</FormField>
								<FormField label={t("vatRate")}>
									<SearchableSelect
										value={line.vatGroup}
										options={vatGroups.map((v) => ({
											value: v.code,
											label: `${v.code} — %${v.rate} ${v.name}`,
										}))}
										initialOption={
											line.vatGroup && !vatGroups.some((v) => v.code === line.vatGroup)
												? { value: line.vatGroup, label: line.vatGroup }
												: undefined
										}
										placeholder={t("search")}
										clearable
										onChange={(code) => {
											const g = vatGroups.find((v) => v.code === code);
											setLines((prev) => {
												const next = [...prev];
												if (next[idx])
													next[idx] = {
														...next[idx],
														vatGroup: code,
														...(g ? { vatRate: String(g.rate) } : {}),
													};
												return next;
											});
										}}
									/>
								</FormField>
								<FormField label={t("discountPercent")}>
									<Input
										placeholder={t("discountPercent")}
										type="number"
										value={line.discountPercent}
										onChange={(e) => updateLine(idx, "discountPercent", e.target.value)}
									/>
								</FormField>
							</div>
							<div className={styles.lineActions}>
								<span className={styles.lineTotal}>{calcLineTotal(line).toFixed(2)}</span>
								{lines.length > 1 && (
									<Button
										variant="ghost"
										size="sm"
										className={styles.removeLineBtn}
										onClick={() => removeLine(idx)}
									>
										<Minus size={16} />
									</Button>
								)}
							</div>
						</div>
					))}
					<Button variant="secondary" className={styles.addLineBtn} onClick={addLine}>
						<Plus size={16} />
					</Button>
				</Card>

				<Card>
					<div className={styles.totals}>
						<div className={styles.totalRow}>
							<span>{t("subtotal")}</span>
							<span>
								{subtotal.toFixed(2)} {header.currency}
							</span>
						</div>
						<div className={styles.totalRow}>
							<span>{t("vatTotal")}</span>
							<span>
								{vatTotal.toFixed(2)} {header.currency}
							</span>
						</div>
						<div className={[styles.totalRow, styles.grandTotal].join(" ")}>
							<span>{t("total")}</span>
							<span>
								{total.toFixed(2)} {header.currency}
							</span>
						</div>
					</div>
					<div className={styles.submitRow}>
						<Button
							onClick={submit}
							isLoading={isEdit ? updateOrder.isPending : createOrder.isPending}
						>
							{isEdit ? t("save") : t("create")}
						</Button>
					</div>
				</Card>
			</div>
		</div>
	);
}
